import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Tag, Save, X, Pencil, Image, Video } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import RichTextEditor from './RichTextEditor';
import { db } from '../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';

const CATEGORIAS = [
  'Direito', 'Direito Constitucional', 'Direito Penal', 'Direito Civil',
  'Direito Trabalhista', 'Direito Tributário', 'Direito Previdenciário',
  'Direito do Consumidor', 'Direito Administrativo', 'Direito Digital', 'Direito Internacional',
];

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const VisualEditor = ({ noticia, onCancelar, onSalvo }) => {
  const [titulo, setTitulo] = useState(noticia.titulo || '');
  const [subtitulo, setSubtitulo] = useState(noticia.subtitulo || '');
  const [conteudoHtml, setConteudoHtml] = useState(noticia.conteudo_html || '');
  const [tagsText, setTagsText] = useState(
    Array.isArray(noticia.tags) ? noticia.tags.join(', ') : (noticia.tags || '')
  );
  const [fonte, setFonte] = useState(noticia.fonte || '');
  const [imagemCapa, setImagemCapa] = useState(noticia.imagem_capa || '');
  const [videoUrl, setVideoUrl] = useState(noticia.video_url || '');
  const [categoria, setCategoria] = useState(noticia.categoria || 'Direito');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [editandoImagem, setEditandoImagem] = useState(!noticia.imagem_capa);
  const [editandoVideo, setEditandoVideo] = useState(false);

  const tituloRef = useRef(null);

  // Auto-resize do textarea do título
  useEffect(() => {
    if (tituloRef.current) {
      tituloRef.current.style.height = 'auto';
      tituloRef.current.style.height = tituloRef.current.scrollHeight + 'px';
    }
  }, [titulo]);

  // Bloqueia scroll do body
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Fechar com Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancelar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancelar]);

  const dataFormatada = noticia.data?.toDate
    ? noticia.data.toDate().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  const embedUrl = getEmbedUrl(videoUrl);
  const tagsArray = tagsText.split(',').map((t) => t.trim()).filter(Boolean);

  const handleSalvar = async () => {
    if (!titulo.trim()) {
      setErro('O título é obrigatório.');
      return;
    }
    if (!conteudoHtml || conteudoHtml === '<p><br></p>') {
      setErro('O texto da notícia é obrigatório.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      await updateDoc(doc(db, 'noticias', noticia.id), {
        titulo: titulo.trim(),
        subtitulo: subtitulo.trim(),
        conteudo_html: conteudoHtml,
        tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean),
        fonte: fonte.trim(),
        imagem_capa: imagemCapa.trim(),
        video_url: videoUrl.trim(),
        categoria,
      });
      onSalvo();
    } catch {
      setErro('Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const conteudo = (
    <div className="fixed inset-0 z-[9999] bg-gray-50 overflow-y-auto overflow-x-hidden">

      {/* ── Barra de ações ── */}
      <div className="sticky top-0 z-50 bg-[#0F172A] text-white px-6 py-3 flex flex-wrap items-center justify-between gap-3 shadow-md">
        <div className="flex items-center gap-2.5">
          <Pencil size={15} className="text-[#B49163]" />
          <span className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-gray-300">
            Modo de Edição Visual
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {erro && (
            <span className="text-xs text-red-400 font-semibold bg-red-950/40 px-3 py-1 rounded-lg">
              {erro}
            </span>
          )}
          <button
            type="button"
            onClick={handleSalvar}
            disabled={salvando}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold px-4 py-2 rounded-lg text-xs sm:text-sm shadow-md transition-colors"
          >
            <Save size={14} />
            {salvando ? 'Salvando...' : 'Salvar Alterações'}
          </button>
          <button
            type="button"
            onClick={onCancelar}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold text-gray-300 border border-gray-600 hover:bg-white/10 transition-colors"
          >
            <X size={14} />
            Cancelar Edição
          </button>
        </div>
      </div>

      {/* Header real do site */}
      <Header />

      <main className="bg-gray-50 min-h-screen">
        <article className="max-w-4xl mx-auto px-6 py-12">

          {/* ── Metadados: Categoria, Data, Fonte ── */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-6">
            <div className="flex items-center gap-2">
              <Tag size={17} className="text-[#B49163]" />
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                className="text-sm font-semibold text-[#B49163] bg-transparent border-b border-dashed border-[#B49163]/50 focus:outline-none focus:border-[#B49163] cursor-pointer"
              >
                {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Calendar size={15} />
              <span>{dataFormatada}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-400">Fonte:</span>
              <input
                type="text"
                value={fonte}
                onChange={(e) => setFonte(e.target.value)}
                placeholder="Redação"
                className="text-sm text-gray-600 bg-transparent border-b border-dashed border-gray-300 focus:outline-none focus:border-[#B49163] placeholder:text-gray-300 w-36"
              />
            </div>
          </div>

          {/* ── Título editável (textarea transparente) ── */}
          <textarea
            ref={tituloRef}
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Título da notícia..."
            rows={1}
            className="w-full resize-none overflow-hidden bg-transparent text-4xl md:text-5xl font-serif font-bold text-[#0F172A] mb-4 leading-tight focus:outline-none placeholder:text-gray-300 border-b-2 border-dashed border-transparent focus:border-[#B49163]/40 transition-colors duration-200"
          />

          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
            Subtítulo (opcional)
          </label>
          <input
            type="text"
            value={subtitulo}
            onChange={(e) => setSubtitulo(e.target.value)}
            placeholder="Linha de apoio ao título..."
            className="w-full text-xl md:text-2xl text-slate-600 bg-transparent border-b border-dashed border-gray-200 focus:border-[#B49163]/50 focus:outline-none placeholder:text-gray-300 mb-8 pb-1"
          />

          {/* ── Tags ── */}
          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-2">
              {tagsArray.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-[#0F172A] text-white rounded-full text-sm font-semibold shadow-md"
                >
                  {tag}
                </span>
              ))}
              <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1.5 border border-dashed border-gray-300">
                <Tag size={12} className="text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={tagsText}
                  onChange={(e) => setTagsText(e.target.value)}
                  placeholder="tag1, tag2, tag3..."
                  className="text-xs text-gray-600 bg-transparent focus:outline-none placeholder:text-gray-400 w-36"
                />
              </div>
            </div>
          </div>

          {/* ── Imagem de Capa ── */}
          <div className="mb-8">
            {imagemCapa && !editandoImagem ? (
              <div className="relative group">
                <div className="w-full max-w-3xl rounded-lg overflow-hidden shadow-lg">
                  <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                    <img
                      src={imagemCapa}
                      alt={titulo}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                </div>
                <button
                  onClick={() => setEditandoImagem(true)}
                  className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-semibold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Pencil size={12} /> Alterar imagem
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 hover:border-[#B49163]/50 rounded-xl bg-white transition-colors">
                <Image size={20} className="text-gray-400 flex-shrink-0" />
                <input
                  type="url"
                  value={imagemCapa}
                  onChange={(e) => setImagemCapa(e.target.value)}
                  placeholder="URL da imagem de capa (https://...)"
                  className="flex-1 text-sm text-gray-700 bg-transparent focus:outline-none placeholder:text-gray-400"
                  autoFocus={editandoImagem}
                />
                {imagemCapa && (
                  <button
                    onClick={() => setEditandoImagem(false)}
                    className="text-xs text-emerald-600 font-bold hover:underline flex-shrink-0"
                  >
                    ✓ OK
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ── Corpo da notícia (Quill sem bordas externas) ── */}
          <div className="mb-12 ve-quill">
            <RichTextEditor
              value={conteudoHtml}
              onChange={setConteudoHtml}
              placeholder="Escreva o conteúdo completo da notícia aqui..."
            />
          </div>

          {/* ── Vídeo ── */}
          <div className="mb-12">
            {embedUrl && !editandoVideo ? (
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-4">Vídeo</h3>
                <div className="relative group">
                  <div className="relative w-full rounded-xl overflow-hidden shadow-lg" style={{ paddingTop: '56.25%' }}>
                    <iframe
                      src={embedUrl}
                      title="Vídeo da notícia"
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <button
                    onClick={() => setEditandoVideo(true)}
                    className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-semibold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Pencil size={12} /> Alterar vídeo
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 hover:border-[#B49163]/50 rounded-xl bg-white transition-colors">
                <Video size={20} className="text-gray-400 flex-shrink-0" />
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="URL do vídeo (YouTube ou Vimeo) — opcional"
                  className="flex-1 text-sm text-gray-700 bg-transparent focus:outline-none placeholder:text-gray-400"
                />
                {videoUrl && (
                  <button
                    onClick={() => setEditandoVideo(false)}
                    className="text-xs text-emerald-600 font-bold hover:underline flex-shrink-0"
                  >
                    ✓ OK
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-6 mt-12">
            <p className="text-sm text-gray-400 italic">
              Modo de edição visual — as alterações ainda não foram salvas.
            </p>
          </div>
        </article>
      </main>

      <Footer />

      <style>{`
        .ve-quill .ql-toolbar {
          border-left: none !important;
          border-right: none !important;
          border-top: none !important;
          border-bottom: 1px solid #e5e7eb !important;
          background: #f9fafb;
          border-radius: 0 !important;
        }
        .ve-quill .ql-container {
          border: none !important;
          font-size: 1.125rem;
          line-height: 1.75rem;
          font-family: inherit;
        }
        .ve-quill .ql-editor {
          padding: 1.25rem 0;
          min-height: 280px;
          color: #1e293b;
        }
        .ve-quill .ql-editor p,
        .ve-quill .ql-editor h1,
        .ve-quill .ql-editor h2,
        .ve-quill .ql-editor h3 {
          margin-bottom: 1.25rem;
          margin-left: 0;
          padding-left: 0;
          text-indent: 0;
        }
        .ve-quill .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: italic;
          left: 0;
        }
      `}</style>
    </div>
  );

  return createPortal(conteudo, document.body);
};

export default VisualEditor;
